import { useState } from 'react';
import { useApiServices } from '@shared/lib/use-api-services';
import { ApiError } from '@shared/api/errors/errors';
import {
  AUDIT_EXPORT_ROW_LIMIT,
  countCsvDataRows,
  csvFilenameStamp,
  downloadBlob,
  filenameFromDisposition,
} from '@shared/lib/download-blob';
import { toExportSearch, type AuditLogFilters } from '@entities/audit-log';

export interface AuditExportResult {
  rows: number;
  truncated: boolean;
}

export function useAuditLogExport(filters: AuditLogFilters) {
  const services = useApiServices();
  const [isExporting, setIsExporting] = useState(false);

  const exportCsv = async (): Promise<AuditExportResult> => {
    setIsExporting(true);
    try {
      const { blob, headers } = await services.auditLog.exportCsv(toExportSearch(filters));
      const filename =
        filenameFromDisposition(headers.get('content-disposition')) ??
        `audit-log-${csvFilenameStamp()}.csv`;
      const counted = await countCsvDataRows(blob);
      const headerRows = headers.get('x-audit-rows');
      const parsedRows = headerRows != null && headerRows !== '' ? Number(headerRows) : counted;
      const rows = Number.isFinite(parsedRows) ? parsedRows : counted;
      const truncatedHeader = headers.get('x-audit-truncated');
      const truncated =
        truncatedHeader === 'true' ||
        (truncatedHeader == null && counted >= AUDIT_EXPORT_ROW_LIMIT);
      downloadBlob(blob, filename);
      return { rows, truncated };
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw error;
    } finally {
      setIsExporting(false);
    }
  };

  return { exportCsv, isExporting };
}
